import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { profileOptions as defaultProfileOptions, capitalOptions, horizonOptions } from "@/data/content";

const fieldCls =
  "bg-[#1E2229] border-white/10 text-white placeholder:text-white/30 focus:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059] rounded-none h-12";

function isEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}
function isPhone(v) {
  return /^[+()\d][\d\s()\-.]{6,}$/.test(v.trim());
}

export function LeadForm({
  businessLine = null,
  sourcePage,
  showProfile = false,
  showInvestorFields = false,
  fixedProfile = null,
  variant = "corporate", // "corporate" | "empathetic"
  submitLabel = "Invia richiesta",
  profileOptions = defaultProfileOptions,
  messagePlaceholder = "Il tuo messaggio o note (opzionale)",
  privacyPrefix = "Ho letto e accetto la ",
  privacyLinkLabel = "Privacy Policy",
  privacySuffix = " e autorizzo il trattamento dei miei dati (GDPR).",
}) {
  const rounded = variant === "empathetic" ? "rounded-lg" : "rounded-none";
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    profile: fixedProfile || "",
    capital: "",
    horizon: "",
    message: "",
    privacy_consent: false,
    company: "", // honeypot
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [serverError, setServerError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Campo obbligatorio";
    if (!form.last_name.trim()) e.last_name = "Campo obbligatorio";
    if (!isEmail(form.email)) e.email = "Email non valida";
    if (!isPhone(form.phone)) e.phone = "Telefono non valido";
    if (showProfile && !fixedProfile && !form.profile) e.profile = "Seleziona un profilo";
    if (!form.privacy_consent) e.privacy_consent = "Consenso obbligatorio";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setStatus("sending");
    try {
      await api.post("/leads", {
        ...form,
        profile: fixedProfile || form.profile || null,
        business_line: businessLine,
        source_page: sourcePage,
      });
      setStatus("done");
    } catch (err) {
      setServerError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div
        data-testid="lead-success"
        className={`bg-[#1E2229] border border-[#C5A059]/30 ${rounded} p-10 text-center`}
      >
        <CheckCircle2 className="mx-auto text-[#C5A059]" size={44} strokeWidth={1.5} />
        <h3 className="font-display text-2xl mt-5 text-white">Richiesta ricevuta</h3>
        <p className="text-gray-300 mt-3 max-w-md mx-auto leading-relaxed">
          Grazie. Un socio la contatterà a breve, con la riservatezza e l'attenzione che il suo
          caso merita. Nel frattempo, nessuna pressione.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-form" className="space-y-5" noValidate>
      {/* honeypot */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={form.company}
        onChange={(e) => set("company", e.target.value)}
        className="hidden"
        aria-hidden="true"
      />

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Input
            data-testid="lead-first-name"
            placeholder="Nome"
            className={`${fieldCls} ${rounded}`}
            value={form.first_name}
            onChange={(e) => set("first_name", e.target.value)}
          />
          {errors.first_name && <p className="text-red-400 text-xs mt-1">{errors.first_name}</p>}
        </div>
        <div>
          <Input
            data-testid="lead-last-name"
            placeholder="Cognome"
            className={`${fieldCls} ${rounded}`}
            value={form.last_name}
            onChange={(e) => set("last_name", e.target.value)}
          />
          {errors.last_name && <p className="text-red-400 text-xs mt-1">{errors.last_name}</p>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Input
            data-testid="lead-email"
            type="email"
            placeholder="Email"
            className={`${fieldCls} ${rounded}`}
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
          {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
        </div>
        <div>
          <Input
            data-testid="lead-phone"
            placeholder="Telefono"
            className={`${fieldCls} ${rounded}`}
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
          />
          {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
        </div>
      </div>

      {showProfile && !fixedProfile && (
        <div>
          <Select value={form.profile} onValueChange={(v) => set("profile", v)}>
            <SelectTrigger data-testid="lead-profile" className={`${fieldCls} ${rounded}`}>
              <SelectValue placeholder="Il tuo profilo" />
            </SelectTrigger>
            <SelectContent className="bg-[#1E2229] border-white/10 text-white">
              {profileOptions.map((p) => (
                <SelectItem key={p} value={p} data-testid={`profile-opt-${p}`}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.profile && <p className="text-red-400 text-xs mt-1">{errors.profile}</p>}
        </div>
      )}

      {showInvestorFields && (
        <div className="grid sm:grid-cols-2 gap-5">
          <Select value={form.capital} onValueChange={(v) => set("capital", v)}>
            <SelectTrigger data-testid="lead-capital" className={`${fieldCls} ${rounded}`}>
              <SelectValue placeholder="Capitale indicativo" />
            </SelectTrigger>
            <SelectContent className="bg-[#1E2229] border-white/10 text-white">
              {capitalOptions.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={form.horizon} onValueChange={(v) => set("horizon", v)}>
            <SelectTrigger data-testid="lead-horizon" className={`${fieldCls} ${rounded}`}>
              <SelectValue placeholder="Orizzonte temporale" />
            </SelectTrigger>
            <SelectContent className="bg-[#1E2229] border-white/10 text-white">
              {horizonOptions.map((h) => (
                <SelectItem key={h} value={h}>
                  {h}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Textarea
        data-testid="lead-message"
        placeholder={messagePlaceholder}
        className={`${fieldCls} ${rounded} min-h-[120px] py-3`}
        value={form.message}
        onChange={(e) => set("message", e.target.value)}
      />

      <label className="flex items-start gap-3 cursor-pointer select-none">
        <Checkbox
          data-testid="lead-privacy"
          checked={form.privacy_consent}
          onCheckedChange={(v) => set("privacy_consent", !!v)}
          className="mt-1 border-white/30 data-[state=checked]:bg-[#C5A059] data-[state=checked]:border-[#C5A059] rounded-none"
        />
        <span className="text-sm text-gray-400 leading-relaxed">
          {privacyPrefix}
          <Link to="/privacy" className="text-[#C5A059] underline underline-offset-2">
            {privacyLinkLabel}
          </Link>
          {privacySuffix}
        </span>
      </label>
      {errors.privacy_consent && (
        <p className="text-red-400 text-xs -mt-2">{errors.privacy_consent}</p>
      )}

      {serverError && (
        <p data-testid="lead-error" className="text-red-400 text-sm">
          {serverError}
        </p>
      )}

      <Button
        type="submit"
        data-testid="lead-submit"
        disabled={status === "sending"}
        className={`bg-[#C5A059] text-[#0D1B2A] hover:bg-[#D8B978] ${rounded} font-medium px-8 h-12 w-full sm:w-auto`}
      >
        {status === "sending" ? (
          <>
            <Loader2 className="animate-spin mr-2" size={18} /> Invio in corso…
          </>
        ) : (
          submitLabel
        )}
      </Button>
    </form>
  );
}
